import {
  ANSWER_TYPE,
  BUDGET_TIER,
  PROJECT_TYPE_DISPLAY_PLACE,
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_MAX,
} from "features/ProjectType/core";
import * as gql from "hooks/projectType";
import { firstGraphQLError, parseGraphQLError } from "hooks/apollo/graphqlError";

// BE project_config.proto qua GraphQL api_gateway (Query/Mutation.projectConfig, field ops có @permissions PROJECT_CONFIG).
// Tham số cuối của mỗi adapter là Apollo client do data/hooks.js truyền vào (useAppApolloClient).
const pageParams = ({ page = 1, size = PAGE_SIZE_DEFAULT, keyword } = {}) => ({
  page,
  size: Math.min(size, PAGE_SIZE_MAX),
  ...(keyword?.trim() ? { keyword: keyword.trim() } : {}),
});
export const listProjectTypes = (params, client) => gql.gqlListProjectTypes(client, pageParams(params));
export const createProjectType = (payload, client) => gql.gqlCreateProjectType(client, payload);
export const getProjectType = (id, client) => gql.gqlGetProjectType(client, id);
export const updateProjectType = (id, payload, client) => gql.gqlUpdateProjectType(client, id, payload);
export const setProjectTypeVisibility = (id, isVisible, client) =>
  gql.gqlSetProjectTypeVisibility(client, id, isVisible);
export const listProjectQuestions = (id, client) => gql.gqlListProjectQuestions(client, id);
export const createProjectQuestion = (id, payload, client) => gql.gqlCreateProjectQuestion(client, id, payload);
export const updateProjectQuestion = (id, payload, client) => gql.gqlUpdateProjectQuestion(client, id, payload);
export const deleteProjectQuestion = (id, client) => gql.gqlDeleteProjectQuestion(client, id);
export const reorderProjectQuestions = (id, questionIds, client) =>
  gql.gqlReorderProjectQuestions(client, id, questionIds);
export const listProjectPackages = (id, client) => gql.gqlListProjectPackages(client, id);
export const getProjectPackage = (id, client) => gql.gqlGetProjectPackage(client, id);
export const createProjectPackage = (projectTypeId, payload, client) =>
  gql.gqlCreateProjectPackage(client, { ...payload, projectTypeId });
export const updateProjectPackage = (id, payload, client) => gql.gqlUpdateProjectPackage(client, id, payload);
export const deleteProjectPackage = (id, client) => gql.gqlDeleteProjectPackage(client, id);
export const setProjectPackageVisibility = (id, isVisible, client) =>
  gql.gqlSetProjectPackageVisibility(client, id, isVisible);
export const listCatalogProducts = (params, client) => gql.gqlSearchCatalogProducts(client, pageParams(params));

const DEFAULT_ERROR_MESSAGE = "Không tải được dữ liệu. Vui lòng thử lại.";
// Directive @staffSecured/@permissions của gateway trả errorz.PermissionDenied (không có project_code).
const PERMISSION_DENIED = /không có quyền|permission denied/i;
const fromGraphQL = (gqlError) => {
  const parsed = parseGraphQLError(gqlError);
  const projectCode = (gqlError.extensions?.details || []).find((item) => item?.metadata?.project_code)?.metadata
    .project_code;
  const code = projectCode || (PERMISSION_DENIED.test(parsed.message) ? "FORBIDDEN" : undefined);
  return {
    code,
    message: parsed.message,
    errors: parsed.fields.map(({ field, message }) => ({ field, message })),
  };
};
const errorBody = (error) => {
  if (!error) return {};
  const gqlError = firstGraphQLError(error);
  if (gqlError) return fromGraphQL(gqlError);
  // Lỗi mạng/hệ thống của Apollo: không lộ "Failed to fetch", dùng thông báo mặc định như REST.
  if (error.networkError || error.name === "ApolloError") return {};
  return error;
};

export function parseApiError(error) {
  const body = errorBody(error);
  return {
    code: body.code,
    message: body.message || DEFAULT_ERROR_MESSAGE,
    fieldErrors: Object.fromEntries(
      Object.entries(
        body.fieldErrors || Object.fromEntries((body.errors || []).map(({ field, message }) => [field, message]))
      ).map(([field, message]) => [field.replace(/\[(\d+)\]/g, ".$1"), message])
    ),
  };
}
const nullable = (value) => value?.trim() || null;
const empty = (value) => value == null || value === "";
const validationError = (fieldErrors) => {
  if (Object.keys(fieldErrors).length) {
    const error = new Error(Object.values(fieldErrors)[0]);
    Object.assign(error, { code: "VALIDATION_ERROR", fieldErrors });
    throw error;
  }
};
export function buildProjectTypeCreatePayload(form) {
  return {
    name: form.name.trim(),
    shortDescription: nullable(form.shortDescription),
    ...(!empty(form.displayOrder) ? { displayOrder: Number(form.displayOrder) } : {}),
    displayPlace: form.displayPlace || PROJECT_TYPE_DISPLAY_PLACE.CARD,
  };
}
export function buildProjectTypeUpdatePayload(form, current) {
  validationError(
    empty(form.displayOrder) || !Number.isInteger(Number(form.displayOrder)) || Number(form.displayOrder) < 0
      ? { displayOrder: "Vui lòng nhập thứ tự là số nguyên ≥0" }
      : {}
  );
  return {
    name: form.name.trim(),
    shortDescription: nullable(form.shortDescription),
    displayOrder: Number(form.displayOrder),
    displayPlace: form.displayPlace,
    isVisible: form.isVisible,
    icon: current.icon,
  };
}
export function buildQuestionPayload(form) {
  const isChoice = [ANSWER_TYPE.SINGLE_CHOICE, ANSWER_TYPE.MULTI_CHOICE].includes(form.answerType);
  return {
    label: form.label.trim(),
    answerType: form.answerType,
    isRequired: Boolean(form.isRequired),
    unit: form.answerType === ANSWER_TYPE.NUMBER ? nullable(form.unit) : null,
    options: isChoice ? (form.options || []).map((value) => value.trim()) : [],
    placeholder: [ANSWER_TYPE.NUMBER, ANSWER_TYPE.SHORT_TEXT].includes(form.answerType)
      ? nullable(form.placeholder)
      : null,
    displayOrder: Number(form.displayOrder),
  };
}
export function buildPackagePayload(form) {
  const from = empty(form.priceFromMillion) ? null : Number(form.priceFromMillion);
  const to = empty(form.priceToMillion) ? null : Number(form.priceToMillion);
  const errors = {};
  if (from == null && to == null) errors.priceFromMillion = "Vui lòng nhập ít nhất một đầu giá";
  if (from != null && (!Number.isFinite(from) || from < 0)) errors.priceFromMillion = "Giá phải ≥0";
  if (to != null && (!Number.isFinite(to) || to < 0)) errors.priceToMillion = "Giá phải ≥0";
  if (from != null && to != null && from > to) errors.priceToMillion = "Giá từ không được lớn hơn giá đến";
  ["name", "title"].forEach((key) => {
    if (!form[key]?.trim() || form[key].trim().length > 255) errors[key] = "Vui lòng nhập từ 1 đến 255 ký tự";
  });
  if (!Object.values(BUDGET_TIER).includes(form.budgetTier)) errors.budgetTier = "Vui lòng chọn mức ngân sách";
  if (form.priceNote?.length > 255) errors.priceNote = "Tối đa 255 ký tự";
  const ids = new Set();
  // Q-H: BE resolves catalog prices at save time; unitPrice and version never belong to this write DTO.
  const products = (form.products || []).map((product, index) => {
    const field = `products.${index}`;
    if (ids.has(product.productId)) errors[`${field}.productId`] = "Sản phẩm đã có trong gói";
    ids.add(product.productId);
    if (!Number.isInteger(product.quantity) || product.quantity <= 0)
      errors[`${field}.quantity`] = "Số lượng phải là số nguyên >0";
    if (product.displayLabel?.length > 50) errors[`${field}.displayLabel`] = "Nhãn tối đa 50 ký tự";
    return { productId: product.productId, quantity: product.quantity, displayLabel: nullable(product.displayLabel) };
  });
  validationError(errors);
  return {
    budgetTier: form.budgetTier,
    name: form.name.trim(),
    title: form.title.trim(),
    shortDescription: nullable(form.shortDescription),
    priceFromMillion: from,
    priceToMillion: to,
    priceNote: nullable(form.priceNote),
    isVisible: Boolean(form.isVisible),
    products,
  };
}
